import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import EmbeddedNav from "../components/EmbeddedNav";
import Accordion from "../components/ui/Accordion";
import PageHero from "../components/ui/PageHero";
import usePageMeta from "../hooks/usePageMeta";

const faqItems = [
  { id: "g1", category: "General", question: "Who can apply for a loan?", answer: "South African adults with valid ID, active bank account, and regular income may apply." },
  { id: "g2", category: "General", question: "How quickly can I get feedback?", answer: "Most applications are reviewed quickly once required information is complete." },
  { id: "a1", category: "Application", question: "What documents are required?", answer: "ID, proof of income, and supporting banking details are commonly required." },
  { id: "a2", category: "Application", question: "Can I apply on mobile?", answer: "Yes. The application flow is designed to work well on mobile devices." },
  { id: "f1", category: "Fees", question: "What fees are included?", answer: "Typical estimates include interest, initiation fee, and monthly service fee." },
  { id: "f2", category: "Fees", question: "Are there hidden fees?", answer: "No hidden fees are intended. Final terms are provided before you proceed." },
  { id: "r1", category: "Repayment", question: "Can I repay early?", answer: "Early repayment is generally possible and can reduce interest costs." },
  { id: "r2", category: "Repayment", question: "How are repayments calculated?", answer: "Repayment estimates are based on principal, monthly rate, term, and applicable fees." },
  { id: "r3", category: "Repayment", question: "What if I miss a payment?", answer: "Contact support immediately so available options can be discussed." },
  { id: "a3", category: "Application", question: "Can my application be declined?", answer: "Yes. Approval depends on affordability checks and submitted information." },
];

const categories = ["All", "General", "Fees", "Repayment", "Application"];

export default function FaqPage() {
  usePageMeta("FAQ | Zebra MicroWealth", "Frequently asked questions about loans, fees, applications, and repayments.");

  const [category, setCategory] = useState("All");
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    return faqItems.filter((item) => {
      const inCategory = category === "All" || item.category === category;
      const q = query.trim().toLowerCase();
      const inSearch = !q || item.question.toLowerCase().includes(q) || item.answer.toLowerCase().includes(q);
      return inCategory && inSearch;
    });
  }, [category, query]);

  return (
    <div className="w-full min-h-screen space-y-8 px-6 py-8 md:px-10 lg:py-10">
      <EmbeddedNav />
      <PageHero title="Frequently Asked Questions" subtitle="Find answers by topic or search for a specific question." />

      <section className="rounded-3xl border border-white/60 bg-white/80 p-5 shadow-xl backdrop-blur">
        <div className="flex flex-wrap gap-2">
          {categories.map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => setCategory(item)}
              className={`rounded-full px-4 py-2 text-sm font-medium transition ${
                category === item ? "bg-pink-500 text-white" : "bg-slate-100 text-slate-700"
              }`}
            >
              {item}
            </button>
          ))}
        </div>

        <label className="mt-4 block">
          <span className="sr-only">Search FAQs</span>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search FAQs"
            className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-slate-700"
          />
        </label>
      </section>

      <Accordion items={filtered.length ? filtered : [{ id: "none", question: "No results found", answer: "Try a broader search or switch category." }]} />

      <section className="rounded-3xl border border-pink-100 bg-gradient-to-r from-pink-50 to-white p-6 shadow-lg">
        <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
          <h3 className="text-2xl font-semibold text-slate-800">Still need help?</h3>
          <Link to="/contact" className="inline-flex items-center justify-center rounded-xl bg-gradient-to-b from-pink-400 to-pink-500 px-5 py-3 font-semibold text-white shadow-xl transition hover:brightness-90">
            Contact Support
          </Link>
        </div>
      </section>
    </div>
  );
}
